import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// Mutual Fund Calculator — mirrors reference image
export default function MutualFundCalc() {
  const [initial, setInitial] = useState(20000);
  const [annualCont, setAnnualCont] = useState(0);
  const [monthlyCont, setMonthlyCont] = useState(1000);
  const [rate, setRate] = useState(5);
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [salesCharge, setSalesCharge] = useState(2);
  const [deferredCharge, setDeferredCharge] = useState(0);
  const [opex, setOpex] = useState(0.5);

  const c = useMemo(() => {
    const totalMonths = years * 12 + months;
    // Effective monthly gross return: (1 + rate)^(1/12) - 1
    const gross = Math.pow(1 + rate / 100, 1 / 12) - 1;
    // Operating expenses reduce return
    const netAnnual = (1 + rate / 100) * (1 - opex / 100) - 1;
    const rMon = Math.pow(1 + netAnnual, 1 / 12) - 1;

    // Sales charge deducted from initial + contributions (front-load)
    const initialNet = initial * (1 - salesCharge / 100);
    const monthlyNet = monthlyCont * (1 - salesCharge / 100);
    const annualNet = annualCont * (1 - salesCharge / 100);
    const salesChargePaid = (initial + monthlyCont * totalMonths + annualCont * years) * (salesCharge / 100);

    let bal = initialNet;
    let opExpense = 0;
    for (let m = 1; m <= totalMonths; m++) {
      bal += monthlyNet;
      if (m % 12 === 0) bal += annualNet;
      const gain = bal * rMon;
      const grossGain = bal * gross;
      opExpense += grossGain - gain; // approx opex portion
      bal += gain;
    }

    // Deferred sales charge on ending
    const deferredPaid = bal * (deferredCharge / 100);
    const ending = bal - deferredPaid;

    const totalPrincipal = initial + monthlyCont * totalMonths + annualCont * years;
    const totalContributions = monthlyCont * totalMonths + annualCont * years;
    const netReturn = ending - totalPrincipal;

    // Net IRR: solve (1+r)^(y) equating principal to ending
    const yEq = totalMonths / 12;
    const netIRR = yEq > 0 ? (Math.pow(ending / (totalPrincipal || 1e-9), 1 / yEq) - 1) * 100 : 0;

    const totalCharges = salesChargePaid + opExpense + deferredPaid;

    return { ending, totalPrincipal, totalContributions, netReturn, netIRR,
      salesChargePaid, opExpense, deferredPaid, totalCharges };
  }, [initial, annualCont, monthlyCont, rate, years, months, salesCharge, deferredCharge, opex]);

  return (
    <CalcShell look="cnet" title="Mutual Fund Calculator"
      subtitle="Estimate the ending balance and net return of mutual funds. It can also calculate the internal rate of return (IRR) after accounting for charges and fees."
      about={<>
        <p>The <b>Mutual Fund Calculator</b> shows how sales loads, operating expenses (expense ratio) and deferred sales charges reduce a fund's gross return over your holding period.</p>
      </>}
      slug="mutual-fund"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div>
          <div className="field"><label>Initial investment</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
              <input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1" data-testid="mf-initial"/></div>
          </div>
          <div className="field"><label>Annual contribution</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
              <input type="number" value={annualCont} onChange={e=>setAnnualCont(+e.target.value||0)} className="flex-1"/></div>
          </div>
          <div className="field"><label>Monthly contribution</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
              <input type="number" value={monthlyCont} onChange={e=>setMonthlyCont(+e.target.value||0)} className="flex-1"/></div>
          </div>
          <div className="field"><label>Rate of return <span className="text-slate-400" title="Expected gross annual return of the fund before fees">?</span></label>
            <div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% /year</span></div>
          </div>
          <div className="field"><label>Holding length</label>
            <div className="flex gap-2 items-center">
              <input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="w-24"/><span className="text-slate-500">years</span>
              <input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)} className="w-24"/><span className="text-slate-500">months</span>
            </div>
          </div>
          <div className="field"><label>Sales charge <span className="text-slate-400" title="Front-end load charged on each purchase">?</span></label>
            <div className="flex gap-2"><input type="number" step="0.01" value={salesCharge} onChange={e=>setSalesCharge(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
          </div>
          <div className="field"><label>Deferred sales charge <span className="text-slate-400" title="Back-end load charged on redemption">?</span></label>
            <div className="flex gap-2"><input type="number" step="0.01" value={deferredCharge} onChange={e=>setDeferredCharge(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
          </div>
          <div className="field"><label>Operating expenses <span className="text-slate-400" title="Annual expense ratio deducted from fund NAV">?</span></label>
            <div className="flex gap-2"><input type="number" step="0.01" value={opex} onChange={e=>setOpex(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% /year</span></div>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="result-panel">
            <div className="result-title">Results</div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1">Ending value</td><td className="text-right font-bold text-blue-800">{fmt(c.ending)}</td></tr>
                <tr><td className="py-1">Total principal</td><td className="text-right font-semibold">{fmt(c.totalPrincipal)}</td></tr>
                <tr><td className="py-1">Total contributions</td><td className="text-right">{fmt(c.totalContributions)}</td></tr>
                <tr><td className="py-1">Net return</td><td className="text-right font-semibold text-emerald-700">{fmt(c.netReturn)}</td></tr>
                <tr><td className="py-1">Net IRR</td><td className="text-right">{c.netIRR.toFixed(3)}% per year</td></tr>
                <tr className="border-t"><td className="py-1 pt-2">Sales charge</td><td className="text-right pt-2">{fmt(c.salesChargePaid)}</td></tr>
                <tr><td className="py-1">Operating expenses</td><td className="text-right">{fmt(c.opExpense)}</td></tr>
                {deferredCharge > 0 && <tr><td className="py-1">Deferred sales charge</td><td className="text-right">{fmt(c.deferredPaid)}</td></tr>}
                <tr className="border-t"><td className="py-1 pt-2 font-semibold">Total charges and fees</td><td className="text-right pt-2 font-semibold">{fmt(c.totalCharges)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <PieChart parts={[
              { label: 'Initial investment', value: initial, color: '#2563eb' },
              { label: 'Total contributions', value: c.totalContributions, color: '#10b981' },
              { label: 'Fees and charges', value: c.totalCharges, color: '#ef4444' },
              { label: 'Net return', value: Math.max(0, c.netReturn), color: '#06b6d4' },
            ]} />
          </div>
        </div>
      </div>

      <StepsPanel steps={[
        `Sales load reduces each dollar going in by ${salesCharge}%`,
        `Net annual return after expense ratio ≈ (1 + ${rate}%) × (1 − ${opex}%) − 1`,
        `Ending value grows by monthly compounding of net return`,
        `Deferred charge (if any) applied on redemption`,
        `Total charges: sales + operating + deferred = <b>${fmt(c.totalCharges)}</b>`
      ]} />
    </CalcShell>
  );
}
